'use client';

import { MainContentWrapper } from '@/components/common/layout/main-content-wrapper';
import { AnimatedSidebar } from '@/components/common/navigation/sidebar/animated-sidebar';
import { ChatContainer } from '@/components/features/chat';
import { PaymentStatusDialog } from '@/components/features/core/payments/PaymentStatusDialog';
import { UserInfoDialog } from '@/components/features/core/profile/UserInfoDialog';
import { useSidebar } from '@/context/sidebar-context';
import {
  useCheckUserProfileCompletion,
  useHandleChatNavigation,
  useHandlePaymentStatus,
  useSyncChatUrlState,
} from '@/lib/hooks';
import { usePageViewTracking } from '@/lib/hooks/useMetrics';
import { useSession } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import { z } from 'zod';

const UserInfoSchema = z.object({
  city: z.string().min(1, 'City is required'),
  ageGroup: z.enum(['18-25', '26-35', '36-50', '51+']),
  language: z.enum(['en', 'fr']),
  acceptedPolicies: z.boolean(),
});
export type UserInfoFormData = z.infer<typeof UserInfoSchema>;

export default function Home(): JSX.Element {
  const searchParams = useSearchParams();
  const chatId = searchParams?.get('chatId');
  const { data: session } = useSession();
  const userId = session?.user.id;
  usePageViewTracking('App Home Page', userId, 'Chat');

  const { isInternalNavRef } = useHandleChatNavigation(chatId);
  useSyncChatUrlState(chatId, isInternalNavRef);
  const { showPaymentDialog, setShowPaymentDialog, paymentStatus } =
    useHandlePaymentStatus();
  const { showUserInfoDialog, setShowUserInfoDialog, handleProfileUpdate } =
    useCheckUserProfileCompletion();

  const { isSidebarOpen, isMobile } = useSidebar();

  return (
    <>
      <div className='flex min-h-dvh w-full flex-col overflow-x-hidden'>
        <div className='flex flex-1 overflow-hidden'>
          <AnimatedSidebar />
          <div
            className={`mt-[72px] flex-1 min-w-0 transition-all duration-300 ${
              isSidebarOpen && !isMobile ? 'ml-80' : 'ml-0'
            }`}
          >
            <ChatContainer />
          </div>
        </div>
      </div>

      <PaymentStatusDialog
        open={showPaymentDialog}
        onOpenChange={setShowPaymentDialog}
        status={paymentStatus === 'success' ? 'success' : 'failed'}
      />

      <UserInfoDialog
        open={showUserInfoDialog}
        onOpenChange={setShowUserInfoDialog}
        onProfileUpdate={handleProfileUpdate}
      />
    </>
  );
}
